import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { cx } from '../lib/cx.js';
import { Icon, type IconName } from '../components/icon.js';
import { Wordmark } from '../components/wordmark.js';
import { useLogout, useSession } from '../data/session.js';

/**
 * Navigation for a console used one-handed on a phone and at a desk on a
 * laptop (PLAN §13.1).
 *
 * The same destinations in both layouts, so muscle memory transfers: a bottom
 * bar on mobile where the thumb is, a left rail from `md` up. "New post"
 * appears in both, because it is the reason the app exists.
 */

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** `/` would otherwise match every route. */
  end?: boolean;
}

export const navItems: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/posts', label: 'Posts', icon: 'posts' },
  { to: '/calendar', label: 'Calendar', icon: 'calendar' },
  { to: '/accounts', label: 'Accounts', icon: 'accounts' },
];

export const secondaryItems: NavItem[] = [
  { to: '/analytics', label: 'Analytics', icon: 'analytics' },
  { to: '/media', label: 'Media', icon: 'media' },
  { to: '/settings', label: 'Settings', icon: 'settings' },
];

const COLLAPSE_KEY = 'dintiko.rail.collapsed';

/**
 * Remembers whether the rail is collapsed.
 *
 * Read lazily on first render rather than in an effect, so the rail does not
 * mount wide and then snap narrow on every page load.
 */
function useCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, set] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      // Private mode, or storage disabled. Not worth failing a render over.
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  return [collapsed, set];
}

/** The desktop rail. Hidden below `md`, where the bottom bar takes over. */
export function SideNav() {
  const navigate = useNavigate();
  const session = useSession();
  const logout = useLogout();
  const [collapsed, setCollapsed] = useCollapsed();

  return (
    <nav
      aria-label="Main"
      data-collapsed={collapsed || undefined}
      className={cx(
        'hidden shrink-0 flex-col border-r border-line bg-surface md:flex',
        // Static, as asked. `self-start` is the load-bearing word: the rail is
        // a flex child of a `min-h-dvh` row, so the default `align-items:
        // stretch` made it exactly as tall as the document — and an element as
        // tall as its scrollport can never stick. `self-start` cancels the
        // stretch, `h-dvh` then bounds it to one screen, and `sticky top-0`
        // finally has somewhere to stick.
        //
        // Not `fixed`: that would pull the rail out of flow, and the content
        // column would slide underneath it, needing a left margin manually
        // kept in step with the 68px/240px collapse. Sticky keeps the layout
        // honest and the collapse animation free.
        'md:sticky md:top-0 md:h-dvh md:self-start',
        // `motion-reduce` rather than a blanket transition: the width change is
        // the one piece of movement in the whole shell, and some people get
        // nauseous from it.
        'transition-[width] duration-150 motion-reduce:transition-none',
        collapsed ? 'w-[68px]' : 'w-60',
      )}
    >
      <div
        className={cx(
          'flex min-h-[56px] items-center gap-2 px-3',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? (
          <img
            src="/icons/icon-192.png"
            alt="Dintiko"
            width={26}
            height={26}
            className="rounded-[22%]"
          />
        ) : (
          <>
            <Wordmark size={22} />
            <RailToggle collapsed={collapsed} onToggle={setCollapsed} />
          </>
        )}
      </div>

      {collapsed && (
        <div className="flex justify-center pb-1">
          <RailToggle collapsed={collapsed} onToggle={setCollapsed} />
        </div>
      )}

      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={() => {
            void navigate('/compose');
          }}
          title={collapsed ? 'New post' : undefined}
          className={cx(
            'flex min-h-[var(--aw-tap)] w-full items-center gap-2 rounded-aw bg-accent',
            'text-md font-medium text-on-accent shadow-aw hover:opacity-90',
            collapsed ? 'justify-center px-0' : 'justify-center px-3',
          )}
        >
          <Icon name="plus" size={18} className="shrink-0" />
          <span className={collapsed ? 'sr-only' : undefined}>New post</span>
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3">
        <RailGroup label="Publishing" items={navItems} collapsed={collapsed} />
        <hr className="my-2 border-line" />
        <RailGroup label="Library" items={secondaryItems} collapsed={collapsed} />
      </div>

      {/* Signing out lived only in the phone menu, so on a desktop there was no
          way to do it at all — and no way to see which account you were in. */}
      <div className={cx('border-t border-line p-3', collapsed && 'px-2')}>
        {!collapsed && (
          <p className="truncate px-1 pb-1.5 font-mono text-2xs text-faint">
            {session.data?.email ?? ''}
          </p>
        )}
        <button
          type="button"
          disabled={logout.isPending}
          onClick={() => {
            logout.mutate();
          }}
          title={collapsed ? 'Sign out' : undefined}
          className={cx(
            'flex min-h-[38px] w-full items-center gap-3 rounded-aw px-3 text-sm',
            'text-muted transition-colors hover:bg-raise hover:text-ink',
            collapsed && 'justify-center px-0',
          )}
        >
          <Icon name="signout" size={18} className="shrink-0" />
          <span className={collapsed ? 'sr-only' : undefined}>Sign out</span>
        </button>
      </div>
    </nav>
  );
}

function RailToggle({
  collapsed,
  onToggle,
}: {
  collapsed: boolean;
  onToggle: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
      onClick={() => {
        onToggle(!collapsed);
      }}
      className={cx(
        'grid h-8 w-8 shrink-0 place-items-center rounded-aw text-faint',
        'transition-colors hover:bg-raise hover:text-ink',
        !collapsed && 'ml-auto',
      )}
    >
      <Icon name={collapsed ? 'expand' : 'collapse'} size={18} />
    </button>
  );
}

/**
 * A labelled run of destinations.
 *
 * The label disappears when collapsed rather than becoming a cryptic initial —
 * at 68px the icons have to carry it, and a truncated word is worse than none.
 */
function RailGroup({
  label,
  items,
  collapsed,
}: {
  label: string;
  items: NavItem[];
  collapsed: boolean;
}) {
  return (
    <>
      {!collapsed && (
        <p className="px-3 pt-1 pb-1 font-mono text-2xs tracking-[0.12em] text-faint uppercase">
          {label}
        </p>
      )}
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          {...(item.end ? { end: true } : {})}
          title={collapsed ? item.label : undefined}
          className={({ isActive }) =>
            cx(
              'flex min-h-[38px] items-center gap-3 rounded-aw text-md font-medium',
              'transition-colors',
              collapsed ? 'justify-center px-0' : 'px-3',
              isActive ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-raise hover:text-ink',
            )
          }
        >
          <Icon name={item.icon} size={18} className="shrink-0" />
          <span className={collapsed ? 'sr-only' : undefined}>{item.label}</span>
        </NavLink>
      ))}
    </>
  );
}

/**
 * How tall the mobile bar is.
 *
 * Declared here and set by the layout on an ancestor of both the bar and the
 * content, because the content pads itself by it. It used to be a hardcoded
 * `pb-20` on `<main>` — right by luck, and silently wrong the moment the bar
 * changed height.
 */
export const BOTTOM_NAV_HEIGHT = '64px';

/**
 * The mobile bar. `pb-[env(safe-area-inset-bottom)]` keeps it clear of the
 * home indicator when the PWA is installed full-screen.
 *
 * Four destinations and Create, which is what fits under a thumb. The other
 * three live in the header as icons — see {@link HeaderIcons}. Between them
 * every destination is on the screen; none is behind a word.
 */
export function BottomNav() {
  const navigate = useNavigate();

  return (
    <nav
      aria-label="Main"
      className={cx(
        'fixed inset-x-0 bottom-0 z-40 border-t border-line bg-surface md:hidden',
        'pb-[env(safe-area-inset-bottom)]',
      )}
    >
      <ul className="grid grid-cols-5">
        {navItems.slice(0, 2).map((item) => (
          <BottomItem key={item.to} item={item} />
        ))}

        <li className="grid place-items-center">
          <button
            type="button"
            onClick={() => {
              void navigate('/compose');
            }}
            aria-label="New post"
            className={cx(
              'grid h-11 w-11 -translate-y-3 place-items-center rounded-aw-lg',
              'bg-accent text-on-accent shadow-aw',
            )}
          >
            <Icon name="plus" size={22} />
          </button>
        </li>

        {navItems.slice(2).map((item) => (
          <BottomItem key={item.to} item={item} />
        ))}
      </ul>
    </nav>
  );
}

function BottomItem({ item }: { item: NavItem }) {
  return (
    <li>
      <NavLink
        to={item.to}
        {...(item.end ? { end: true } : {})}
        className={({ isActive }) =>
          cx(
            'flex min-h-[var(--aw-tap)] flex-col items-center justify-center gap-0.5 py-1.5',
            'text-2xs font-medium transition-colors',
            isActive ? 'text-accent' : 'text-muted',
          )
        }
      >
        <Icon name={item.icon} size={21} />
        {item.label}
      </NavLink>
    </li>
  );
}

/**
 * The other three, in the phone header.
 *
 * They were behind a button labelled "More" — the one control on the screen
 * that named nothing, and the reason three of the seven destinations were
 * effectively undiscoverable. Up here they are the hardest reach on a tall
 * phone, which is the right place for the three opened least.
 */
export function HeaderIcons() {
  return (
    <ul className="flex items-center gap-0.5">
      {secondaryItems.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            aria-label={item.label}
            title={item.label}
            className={({ isActive }) =>
              cx(
                'grid h-11 w-11 place-items-center rounded-aw transition-colors',
                isActive ? 'bg-accent-soft text-accent' : 'text-muted',
              )
            }
          >
            <Icon name={item.icon} size={20} />
          </NavLink>
        </li>
      ))}
    </ul>
  );
}
